import { useState, useEffect, useCallback, useContext } from 'react';
import { CollaborationAccessContext } from './useCollaborationAccess';
import { supabase } from '../supabaseClient';

/**
 * Hook para gerenciar o add-on de Sensoriamento Remoto (Satélite)
 *
 * Status possíveis:
 *  - 'loading'    → buscando dados
 *  - 'ativo'      → tem acesso (trial ou pago, dentro da validade)
 *  - 'trial'      → em período de trial (subconjunto de 'ativo', para exibir badge)
 *  - 'expirado'   → já teve acesso mas expirou
 *  - 'bloqueado'  → nunca ativou
 */
export function useAddonSatelite(userId) {
  const { sharedModules } = useContext(CollaborationAccessContext);
  const [status, setStatus] = useState('loading');
  const [expiracao, setExpiracao] = useState(null);
  const [trialUsado, setTrialUsado] = useState(false);
  const [diasRestantes, setDiasRestantes] = useState(null);
  const [loading, setLoading] = useState(true);

  const carregar = useCallback(async () => {
    if (!userId) { setLoading(false); return; }

    try {
      const { data, error } = await supabase
        .from('usuarios')
        .select('plano, plano_trial, plano_data_expiracao, addon_satelite, addon_satelite_trial_usado, addon_satelite_expiracao, addon_satelite_inicio')
        .eq('id', userId)
        .single();

      if (error) throw error;

      const agora = new Date();
      const planoExp = data.plano_data_expiracao ? new Date(data.plano_data_expiracao) : null;
      const planoNovoAtivo = ['essential', 'professional', 'management', 'enterprise'].includes(data.plano)
        && (!planoExp || planoExp > agora);
      const starterTrialAtivo = data.plano === 'starter' && data.plano_trial && planoExp && planoExp > agora;
      const exp = data.addon_satelite_expiracao ? new Date(data.addon_satelite_expiracao) : null;
      const ativo = data.addon_satelite && exp && exp > agora;
      const dias = exp ? Math.ceil((exp - agora) / (1000 * 60 * 60 * 24)) : null;
      const TRIAL_DURACAO_MS = 14 * 24 * 60 * 60 * 1000 + 60_000;
      const ehTrial = ativo && data.addon_satelite_trial_usado &&
        data.addon_satelite_inicio && exp &&
        (exp - new Date(data.addon_satelite_inicio)) <= TRIAL_DURACAO_MS;

      setExpiracao(planoNovoAtivo || starterTrialAtivo ? planoExp : exp);
      setTrialUsado(data.addon_satelite_trial_usado || false);
      setDiasRestantes(dias);

      if (planoNovoAtivo || starterTrialAtivo) {
        setStatus(starterTrialAtivo ? 'trial' : 'plano');
      } else if (ativo) {
        setStatus(ehTrial ? 'trial' : 'ativo');
      } else if (!ativo && exp) {
        setStatus('expirado');
      } else {
        setStatus('bloqueado');
      }
    } catch (err) {
      console.error('Erro ao carregar add-on satélite:', err);
      setStatus('bloqueado');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const ativarTrial = useCallback(async () => {
    if (!userId) return { sucesso: false, mensagem: 'Usuário não autenticado' };
    try {
      const { data, error } = await supabase.rpc('ativar_trial_satelite', { p_user_id: userId });
      if (error) throw error;
      if (data?.sucesso) await carregar();
      return data;
    } catch (err) {
      return { sucesso: false, mensagem: err.message };
    }
  }, [userId, carregar]);

  const temAcesso = sharedModules.includes('sensoriamento') || status === 'ativo' || status === 'trial' || status === 'plano';

  return { status, expiracao, trialUsado, diasRestantes, loading, temAcesso, ativarTrial, recarregar: carregar };
}
